import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Aangifte, BokkzIbDataService, IncomeTaxEntity, OmschrijvingEnBedrag, Staking } from '@snelstart-ventures/fiscaal/domain-logic';
import { FormControl, FormGroup } from '@angular/forms';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { filter, Observable, shareReplay, tap } from 'rxjs';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-ondernemer',
    templateUrl: './ondernemer.component.html',
    styleUrls: ['./ondernemer.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule, CommonModule],
})
export class OndernemerComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    public index = 0;
    public dialogVisible = false;
    public isOndernemer = false;
    public heeftWuoStaking = false;
    public heeftFiscaalPartner = false;
    public isPartner = false;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public meewerkUren: SelectFormControlItem<number>[] = [
        { value: 1, label: 'Nee' },
        { value: 500, label: 'Ja, minder dan 525u' },
        { value: 800, label: 'Ja, 525u - 874u' },
        { value: 1000, label: 'Ja, 875u - 1.224u' },
        { value: 1500, label: 'Ja, 1.225u - 1.749u' },
        { value: 2000, label: 'Ja, 1.750u of meer' },
    ];

    public aantalKerenBijAO: SelectFormControlItem<number>[] = [
        { value: 0, label: 'Geen' },
        { value: 1, label: '1 keer' },
        { value: 2, label: '2 keer' },
        { value: 3, label: 'Meer dan 2 keer' },
    ];

    public formGroup = new FormGroup({
        krijgtZelfstandigenAftrek: new FormControl<boolean | null>(false),
        krijgtStartersAftrek: new FormControl<boolean | null>(null),
        krijgtStartersAftrekBijArbeidsOngeschiktheid: new FormControl<boolean | null>(null),
        aantalKerenStartersaftrekBijAOToegepastInAfgelopenVijfJaar: new FormControl<number | null>(null),
        krijgtSenOaftrek: new FormControl<boolean | null>(null),
        krijgtVerhogingSenOAftrek: new FormControl<boolean | null>(null),
        speurOntwikkelaarVerklaringNummer: new FormControl<string | null>(null),
        krijgtStakingsaftrek: new FormControl<boolean | null>(null),
        bedragEerdereBenutteStakingsaftrek: new FormControl<number | null>(null),
        meewerkUren: new FormControl<number | null>(null),
        oudedagsreserveBeginJaar: new FormControl<number | null>(null),
        forDotatie: new FormControl<number | null>(null),
        vrijvalFORIvmPremieLijfente: new FormControl<number | null>(null),
        restantZelfstandigenaftrekUitEerdereJaren: new FormControl<number | null>(null),
        krijgtWajong: new FormControl<boolean | null>(false),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((partner) => {
            this.isPartner = partner;
            this.incomeTaxEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
                this.heeftFiscaalPartner = entity.aangifte?.gezamenlijkeAangifte ?? false
                if (partner) {
                    this.handleSwitch(entity, true);
                } else {
                    this.handleSwitch(entity, false);
                }
            });
        });
    }

    ngOnDestroy(): void {
        // rely on saveFrom in component
    }

    private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
        const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
        if (details) {
            this.resetAndPatchForms(details);
        }
    }

    private resetAndPatchForms(details: any): void {
        this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
        this.isOndernemer = false;
        if (details.box1?.winstUitOnderneming) {
            this.isOndernemer = true;
            // const stakingsAftrek = details.aangifte?.box1?.winstUitOnderneming?.ondernemingen.some((a) => a.staking);
            // this.heeftWuoStaking = !!stakingsAftrek;

            this.formGroup.patchValue(details.box1?.winstUitOnderneming || {}, { emitEvent: false });
            const restantArr = details.box1?.winstUitOnderneming?.restantZelfstandigenaftrekUitEerdereJaren;
            const restantValue = Array.isArray(restantArr) && restantArr.length > 0 ? restantArr[0].bedrag : null;
            this.formGroup.patchValue({ restantZelfstandigenaftrekUitEerdereJaren: restantValue }, { emitEvent: false });
        }
        this.formGroup.patchValue({ krijgtWajong: details.heffingskortingen?.krijgtWajong }, { emitEvent: false });
        this.setupValidForm();
    }

    private setupValidForm() {
        if (!this.formGroup.get('krijgtZelfstandigenAftrek')?.value) {
            this.formGroup.patchValue({ krijgtStartersAftrek: null }, { emitEvent: false });
            this.formGroup.patchValue({ krijgtSenOaftrek: null }, { emitEvent: false });
            this.formGroup.patchValue({ krijgtVerhogingSenOAftrek: null }, { emitEvent: false });
            this.formGroup.patchValue({ meewerkUren: null }, { emitEvent: false });
        }
        if (this.formGroup.get('krijgtZelfstandigenAftrek')?.value) {
            this.formGroup.patchValue({ krijgtStartersAftrekBijArbeidsOngeschiktheid: null }, { emitEvent: false });
            this.formGroup.patchValue({ aantalKerenStartersaftrekBijAOToegepastInAfgelopenVijfJaar: null }, { emitEvent: false });
        }
        if (this.formGroup.get('krijgtStartersAftrek')?.value) {
            this.formGroup.patchValue({ krijgtStartersAftrekBijArbeidsOngeschiktheid: null }, { emitEvent: false });
        }
        if (this.formGroup.get('krijgtStartersAftrekBijArbeidsOngeschiktheid')?.value) {
            this.formGroup.patchValue({ krijgtStartersAftrek: false }, { emitEvent: false });
        }
        if (!this.formGroup.get('krijgtSenOaftrek')?.value) {
            this.formGroup.patchValue({ speurOntwikkelaarVerklaringNummer: null }, { emitEvent: false });
            this.formGroup.patchValue({ krijgtVerhogingSenOAftrek: null }, { emitEvent: false });
        }
        if (!this.formGroup.get('krijgtStakingsaftrek')?.value) {
            this.formGroup.patchValue({ bedragEerdereBenutteStakingsaftrek: null }, { emitEvent: false });
        }
    }

    saveForm(): void {
        this.setupValidForm();
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        const formOndernemingen = this.formGroup.getRawValue();

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            const update = (aangifte: Aangifte) => {
                const winstUitOnderneming: any = aangifte.box1?.winstUitOnderneming || {};
                const { restantZelfstandigenaftrekUitEerdereJaren, ...restForm } = formOndernemingen;

                // Map to OmschrijvingEnBedrag[] if value is present
                const restantList: OmschrijvingEnBedrag[] | undefined =
                    restantZelfstandigenaftrekUitEerdereJaren != null
                        ? [{ omschrijving: 'Restant zelfstandigenaftrek', bedrag: restantZelfstandigenaftrekUitEerdereJaren }]
                        : undefined;

                return {
                    ...aangifte,
                    box1: {
                        ...aangifte.box1,
                        winstUitOnderneming: {
                            ...winstUitOnderneming,
                            ...restForm,
                            ondernemingen: winstUitOnderneming.ondernemingen ?? [],
                            restantZelfstandigenaftrekUitEerdereJaren: restantList,
                        },
                    },
                    heffingskortingen: {
                        ...aangifte.heffingskortingen,
                        krijgtWajong: formOndernemingen.krijgtWajong,
                    },
                };
            };

            const updatedAangifte = this.isPartner
                ? {
                      ...currentEntity.aangifte,
                      partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
                  }
                : update(currentEntity.aangifte);

            return {
                ...currentEntity,
                aangifte: updatedAangifte,
            } as IncomeTaxEntity;
        });
    }
}
